<template>
  <div
      class="base-fixed-aside__item"
      :class="{
        'is-link': !!link,
        'mounted': isMounted,
      }"
      :style="{
        background: background,
        color: color,
        ...cssVariables,
      }"
  >
    <div class="base-fixed-aside__item-icon">
      <FaIcon :icon="`fa-light fa-${icon}`" />
    </div>
    <div
        ref="contentEl"
        class="base-fixed-aside__item-content"
        v-html="content"
    />
    <div
        v-if="link"
        class="base-fixed-aside__item-chevron"
    >
      <FaIcon icon="fa-light fa-chevron-right" />
    </div>
    <RouterLink
        v-if="link"
        :to="link"
        :target="linkTarget"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, nextTick } from 'vue';

withDefaults(defineProps<{
  icon?: string
  lable?: string
  link?: string
  linkTarget?: string
  color?: string,
  background?: string
  content?: string
}>(), {
  icon: 'messages-question',
  lable: 'Zu den FAQ',
});

const contentEl = ref<HTMLElement>();
const cssVariables = ref<Record<string, string>>({});
const isMounted = ref(false);
const setCssVariables = () => {
  if (contentEl.value === undefined) return;

  contentEl.value?.style.setProperty('height', 'auto');

  const contentElHeight = contentEl.value?.offsetHeight;

  if (!contentElHeight) return;

  cssVariables.value['--content-height'] = `${contentElHeight}px`;

  contentEl.value?.style.removeProperty('height');
};

onMounted(() => {
  setCssVariables();
  nextTick(() => {
    isMounted.value = true;
  });
});
</script>

<style lang="scss">
@import "../../scss/component-helpers";

.base-fixed-aside__item {
  position: relative;
  max-width: 420px;
  display: grid;
  grid-auto-columns: auto;
  grid-template-rows: auto;
  width: fit-content;
  box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
  border-radius: 65px 0 0 65px;
  padding: 10px 0;
  transform: translateX(-80px);
  transition: transform .45s ease-out, border-radius .45s ease-out;
  background: #E7F1F5;
  color: #293033;

  &-icon {
    grid-column: 1 / 2;
    padding-right: 30px;
    font-size: 30px;
    line-height: 0;
    height: 35px;
    align-self: start;
    display: flex;
    align-items: center;
    padding-left: 20px;
    transition: padding-right .45s ease-out;
  }

  &-content {
    grid-column: 2 / 3;
    overflow: hidden;
    font-size: .875rem;
    @include font-weight-fix;
    line-height: 1.25;
    align-self: center;
    width: max-content;
    max-width: 100%;
    padding-right: 30px;
    height: 1em;
    opacity: 0;

    a {
      text-decoration: none;
      color: #293033;
    }
  }

  &.mounted {
    .base-fixed-aside__item-content {
      transition: height .45s ease-out, padding-right .45s ease-out, opacity .45s ease-out;
    }
  }

  &-chevron {
    grid-column: 3 / 4;
    line-height: 1;
    align-self: center;
    padding-right: 20px;
  }

  > a {
    position: absolute;
    inset: 0;
  }

  &.is-link {
    .base-fixed-aside__item-content {
      white-space: nowrap;
      padding-right: 10px;
      @include font-regular(18px);
    }
  }

  &:not(.is-link) {
    &:hover {
      border-radius: 20px 0 0 20px;
    }

    .base-fixed-aside__item-content {
      line-height: 1.5;
    }
  }

  &:hover {
    transform: translateX(-100%);

    .base-fixed-aside__item-content {
      height: var(--content-height);
      opacity: 1;
      padding-right: 20px;
    }

    .base-fixed-aside__item-icon {
      padding-right: 20px;
    }
  }
}
</style>
